import type {GroupCallHistorySession, GroupCallHistoryUserId} from './groupCallHistoryTypes';

export type GroupCallHistoryMedia = 'audio' | 'video';

export const groupCallHistoryMedia = (session: Pick<GroupCallHistorySession, 'intent' | 'participants'>): GroupCallHistoryMedia =>
    session.intent === 'video' || session.participants.some(participant => participant.publishedVideo) ? 'video' : 'audio';

export const groupCallUsedScreenShare = (session: Pick<GroupCallHistorySession, 'participants'>): boolean =>
    session.participants.some(participant => participant.publishedScreen);

export const isGroupCallInProgress = (session: Pick<GroupCallHistorySession, 'endedAt'>): boolean => session.endedAt === null;

const durationSeconds = (session: Pick<GroupCallHistorySession, 'startedAt' | 'endedAt'>): number | null => {
    if (!session.endedAt) {
        return null;
    }
    const started = Date.parse(session.startedAt);
    const ended = Date.parse(session.endedAt);
    if (!Number.isFinite(started) || !Number.isFinite(ended) || ended < started) {
        return null;
    }
    return Math.round((ended - started) / 1000);
};

export const formatGroupCallDuration = (session: Pick<GroupCallHistorySession, 'startedAt' | 'endedAt'>): string => {
    if (isGroupCallInProgress(session)) {
        return 'In progress';
    }
    const seconds = durationSeconds(session);
    if (seconds === null) {
        return 'Duration unavailable';
    }
    if (seconds < 60) {
        return 'Less than a minute';
    }
    const totalMinutes = Math.floor(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) {
        return `${minutes} min`;
    }
    if (minutes === 0) {
        return `${hours} hr`;
    }
    return `${hours} hr ${minutes} min`;
};

const NOTIFICATION_MATCH_MS = 120_000;

export const sessionNearest = (sessions: readonly GroupCallHistorySession[], at: number): GroupCallHistorySession | undefined => {
    let nearest: GroupCallHistorySession | undefined;
    let nearestGap = NOTIFICATION_MATCH_MS;
    sessions.forEach(session => {
        const gap = Math.abs(Date.parse(session.startedAt) - at);
        if (gap <= nearestGap) {
            nearest = session;
            nearestGap = gap;
        }
    });
    return nearest;
};

export const answeredLengthMs = (session: Pick<GroupCallHistorySession, 'startedAt' | 'endedAt' | 'startedByUserId' | 'participants'>): number | undefined => {
    const ended = session.endedAt ? Date.parse(session.endedAt) : NaN;
    const answered = session.participants
        .filter(participant => participant.userId !== session.startedByUserId)
        .map(participant => Date.parse(participant.joinedAt))
        .filter(Number.isFinite)
        .reduce((first, joined) => Math.min(first, joined), Infinity);
    const started = Date.parse(session.startedAt);
    const from = Number.isFinite(started) ? Math.max(answered, started) : answered;
    return Number.isFinite(ended) && Number.isFinite(from) && ended > from ? ended - from : undefined;
};

export const uniqueParticipantIds = (session: Pick<GroupCallHistorySession, 'participants'>): GroupCallHistoryUserId[] => {
    const ids: GroupCallHistoryUserId[] = [];
    session.participants.forEach(participant => {
        if (participant.userId !== null && !ids.includes(participant.userId)) {
            ids.push(participant.userId);
        }
    });
    return ids;
};

export const unmappedParticipantIntervals = (session: Pick<GroupCallHistorySession, 'participants'>): number =>
    session.participants.filter(participant => participant.userId === null).length;

export const describeGroupCallParticipants = (
    session: Pick<GroupCallHistorySession, 'participants' | 'peakParticipants'>,
): string => {
    const count = uniqueParticipantIds(session).length + Math.min(unmappedParticipantIntervals(session), 1);
    if (count === 0) {
        return 'No participants recorded';
    }
    const people = `${count} ${count === 1 ? 'participant' : 'participants'}`;
    const peak = session.peakParticipants;
    return peak > 1 && peak !== count ? `${people} · peak ${peak} devices` : people;
};

export const AVATAR_STACK_VISIBLE = 2;

export const avatarStackLayout = (total: number, visible: number = AVATAR_STACK_VISIBLE): {shown: number; overflow: number} => {
    const shown = Math.max(0, Math.min(total, visible));
    return {shown, overflow: Math.max(0, total - shown)};
};

const pad = (value: number): string => String(value).padStart(2, '0');

export const groupCallHistoryDayKey = (startedAt: string): string => {
    const date = new Date(startedAt);
    if (Number.isNaN(date.getTime())) {
        return 'unknown';
    }
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const groupSessionsByDay = (
    sessions: readonly GroupCallHistorySession[],
): Array<{day: string; sessions: GroupCallHistorySession[]}> => {
    const groups = new Map<string, GroupCallHistorySession[]>();
    [...sessions]
        .sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))
        .forEach(session => {
            const day = groupCallHistoryDayKey(session.startedAt);
            groups.set(day, [...(groups.get(day) ?? []), session]);
        });
    return [...groups.entries()].map(([day, grouped]) => ({day, sessions: grouped}));
};
